import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { getApiInstance } from 'services/Api';

// -----------------------------------------------------------------------------
// Thunk
// -----------------------------------------------------------------------------
// Exports the policy objects as CSV. We fetch it as a Blob so the UI can trigger a download.
export const fetchPolicyObjectsExport = createAsyncThunk<{ data: Blob; fileName: string }, { policyNo: string }>(
	'policyObjectsExport/fetchPolicyObjectsExport',
	async ({ policyNo }) => {
		const apiInstance = await getApiInstance('/commercial-selfservice/');

		// arraybuffer prevents Axios from corrupting UTF-8 Danish characters (Å, Æ, Ø).
		const response = await apiInstance.bffApiServiceLayer.exportPolicyObjects(policyNo, {
			responseType: 'arraybuffer' as any,
			headers: { Accept: 'text/csv' },
		});

		// Decode as UTF-8, swap comma→semicolon for Danish Excel, force CPR (col 2) as text.
		const csv = new TextDecoder('utf-8').decode(response.data as ArrayBuffer);
		const finalCsv = csv
			.replace(/","/g, '";"')
			.replace(/,"/g, ';"')
			.replace(/",/g, '";')
			.split(/\r?\n/)
			.map((line, i) => {
				if (i === 0 || !line.trim()) return line;
				const cols = line.split(';');
				if (cols.length >= 2) cols[1] = `="${cols[1].replace(/"/g, '')}"`;
				return cols.join(';');
			})
			.join('\r\n');

		const blob = new Blob(['\uFEFF' + finalCsv], { type: 'text/csv;charset=utf-8' });
		return {
			data: blob,
			fileName: `policy_objects_${policyNo}.csv`,
		};
	}
);

// -----------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------
interface PolicyObjectsExportState {
	exportBlob: Blob | null;
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PolicyObjectsExportState = {
	exportBlob: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// -----------------------------------------------------------------------------
// Slice
// -----------------------------------------------------------------------------
const policyObjectsExportSlice = createSlice({
	name: 'policyObjectsExport',
	initialState,
	reducers: {
		clearPolicyObjectsExport(state) {
			state.exportBlob = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPolicyObjectsExport.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicyObjectsExport.fulfilled, (state, action) => {
				state.status = HttpStatusCode.OK;
				state.exportBlob = action.payload.data;
			})
			.addCase(fetchPolicyObjectsExport.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Medarbejderliste download fejlede';
			});
	},
});

export const { clearPolicyObjectsExport } = policyObjectsExportSlice.actions;
export default policyObjectsExportSlice.reducer;
